/**
 * El newsletter del proyecto: la lista, sus correos, los artículos y los
 * resultados de cada envío.
 *
 * Qué NO hace el interruptor, a propósito: mandar. Encender el automático
 * autoriza al worker a armar el envío cuando se publique un artículo; no manda
 * nada en ese momento. Un interruptor que enviara al pulsarlo sería un botón
 * de "enviar" disfrazado de preferencia.
 *
 * Por eso encender pide confirmación y apagar no: encender no autoriza UN
 * correo, autoriza todos los que vengan. Frenar siempre tiene que ser fácil.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { SectionHeaderAction } from '../ui/SectionHeaderAction.js';
import { KVRow } from '../ui/KVRow.js';
import { Toggle } from '../ui/Toggle.js';
import { Badge } from '../ui/Badge.js';
import type { BadgeTone } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { EmptyState } from '../ui/EmptyState.js';
import { LoadingState } from '../ui/LoadingState.js';
import { ListItem } from '../ui/ListItem.js';
import { SegmentedControl } from '../ui/SegmentedControl.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { useToast } from '../ui/Toast.js';
import { ImportarListaModal } from './newsletter/ImportarListaModal.js';
import {
  useNewsletter, useMiembrosDelNewsletter, useArticulosDelNewsletter,
  useFijarEnvioAutomatico, useEnviarArticulo,
  type EstadoMiembro, type ArticuloParaEnviar,
} from '../../hooks/useNewsletter.js';

interface Props {
  projectId: number;
}

/** Clave neutra → color. El único mapeo de estados a tono de este módulo. */
const TONO_ENVIO: Record<string, BadgeTone> = {
  draft: 'muted', awaiting_approval: 'amber', approved: 'cyan',
  sending: 'cyan', sent: 'success', cancelled: 'muted', voided: 'danger',
};
const TONO_MIEMBRO: Record<EstadoMiembro, BadgeTone> = {
  subscribed: 'success', unsubscribed: 'muted', bounced: 'amber', complained: 'danger',
};

/** Cuántos correos se listan antes de pedir "ver todos". */
const PRIMEROS = 25;

/**
 * Qué va a pasar con este artículo, dicho siempre.
 *
 * Antes decía «Publicado, sin enviar» a secas, y con el automático encendido
 * eso solo se podía leer como «está roto». Los que se publicaron antes de
 * encenderlo no salen a propósito —encender no puede disparar el archivo
 * entero de golpe— y eso hay que decirlo donde se mira.
 */
function subtituloDelArticulo(a: ArticuloParaEnviar): string {
  if (a.envio) {
    const f = a.envio.sentAt ? new Date(a.envio.sentAt).toLocaleDateString() : '—';
    return `Enviado el ${f} · ${a.envio.entregados} entregados · ${a.envio.fallidos} fallidos`;
  }
  switch (a.motivoSinEnviar) {
    case 'sin_publicar':
      return a.scheduledFor
        ? `Sin publicar · se publica el ${new Date(a.scheduledFor).toLocaleDateString()}`
        : 'Sin publicar y sin fecha';
    case 'anterior_al_automatico':
      return 'No sale solo: se publicó antes de activar el envío automático';
    case 'automatico_apagado':
      return 'Publicado · el envío automático está apagado';
    default:
      return 'Publicado · saldrá en el próximo barrido';
  }
}

export function SeccionNewsletter({ projectId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useNewsletter(projectId);
  const { data: arts } = useArticulosDelNewsletter(projectId);
  const fijarAutomatico = useFijarEnvioAutomatico(projectId);
  const enviar = useEnviarArticulo(projectId);
  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToast();

  const [filtro, setFiltro] = useState<EstadoMiembro>('subscribed');
  const [todos, setTodos] = useState(false);
  const [importando, setImportando] = useState(false);

  const audiencia = data?.audiencia ?? null;
  const conteo = data?.conteo ?? null;
  const envios = data?.envios ?? [];
  const { data: listaMiembros } = useMiembrosDelNewsletter(projectId, filtro, Boolean(audiencia));
  const miembros = listaMiembros?.miembros ?? [];

  const fecha = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '—');

  if (isLoading) {
    return (
      <DetailSection title={t('newsletter.title', { defaultValue: 'Newsletter' })}>
        <LoadingState />
      </DetailSection>
    );
  }

  // Sin lista no es un error ni una lista vacía: este proyecto todavía no
  // tiene newsletter, y decirlo es distinto de mostrar un cero.
  if (!audiencia || !conteo) {
    return (
      <>
        <DetailSection title={t('newsletter.title', { defaultValue: 'Newsletter' })}>
          <EmptyState
            title={t('newsletter.sinLista.title', { defaultValue: 'Este proyecto no tiene lista' })}
            description={t('newsletter.sinLista.description', {
              defaultValue: 'Carga un CSV con los correos y empieza a enviar los artículos publicados.',
            })}
            action={
              <Button onClick={() => setImportando(true)}>
                {t('newsletter.importar.title', { defaultValue: 'Cargar lista de correos' })}
              </Button>
            }
          />
        </DetailSection>
        {importando && (
          <ImportarListaModal projectId={projectId} necesitaRemitente onClose={() => setImportando(false)} />
        )}
      </>
    );
  }

  async function alternarAutomatico(encendido: boolean): Promise<void> {
    if (encendido) {
      const ok = await confirm({
        title: t('newsletter.automatico.confirmar.title', { defaultValue: '¿Activar el envío automático?' }),
        description: t('newsletter.automatico.confirmar.description', {
          defaultValue: 'Desde ahora, cada artículo que se publique saldrá a las {{n}} personas suscritas. No autoriza un correo: autoriza todos los que vengan. Se puede apagar cuando quieras.',
          n: conteo!.suscritos,
        }),
        confirmLabel: t('newsletter.automatico.confirmar.ok', { defaultValue: 'Sí, activar' }),
        tone: 'warning',
      });
      if (!ok) return;
    }
    try {
      await fijarAutomatico.mutateAsync(encendido);
      toast.success(encendido
        ? t('newsletter.automatico.encendido', { defaultValue: 'Envío automático activado' })
        : t('newsletter.automatico.apagado', { defaultValue: 'Envío automático apagado' }));
    } catch {
      toast.error(t('newsletter.automatico.fallo', { defaultValue: 'No se pudo cambiar' }));
    }
  }

  async function mandar(a: ArticuloParaEnviar): Promise<void> {
    const ok = await confirm({
      title: t('newsletter.enviar.confirmar.title', { defaultValue: '¿Enviar este artículo?' }),
      description: t('newsletter.enviar.confirmar.description', {
        defaultValue: '«{{titulo}}» saldrá a {{n}} personas. Un correo enviado no se deshace.',
        titulo: a.title, n: conteo!.suscritos,
      }),
      confirmLabel: t('newsletter.enviar.confirmar.ok', { defaultValue: 'Enviar ahora' }),
      tone: 'warning',
    });
    if (!ok) return;
    try {
      const r = await enviar.mutateAsync({ articleId: a.id, confirmoDestinatarios: conteo!.suscritos });
      toast.success(t('newsletter.enviar.hecho', {
        defaultValue: '{{entregados}} entregados, {{fallidos}} fallidos',
        entregados: r.entregados, fallidos: r.fallidos,
      }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('newsletter.enviar.fallo', { defaultValue: 'No se pudo enviar' }));
    }
  }

  const visibles = todos ? miembros : miembros.slice(0, PRIMEROS);
  const articulos = arts?.articulos ?? [];
  // Un artículo publicado y sin enviar es trabajo pendiente: la tarjeta de
  // artículos arranca abierta solo si hay alguno. Las listas que son registro
  // (correos, historial) arrancan cerradas, con el total en la cabecera.
  const porEnviar = articulos.filter(a => !a.envio && a.status === 'publicado').length;
  const enLaLista = conteo.suscritos + conteo.deBaja + conteo.rebotados + conteo.quejas;

  return (
    <>
      <DetailSection
        title={t('newsletter.title', { defaultValue: 'Newsletter' })}
        collapsible
        storageKey={`newsletter-resumen:${String(projectId)}`}
        titleRight={
          <SectionHeaderAction onClick={() => setImportando(true)}>
            {t('newsletter.importar.mas', { defaultValue: 'Cargar más correos' })}
          </SectionHeaderAction>
        }
      >
        <KVRow label={t('newsletter.lista', { defaultValue: 'Lista' })} value={audiencia.name} />
        <KVRow label={t('newsletter.suscritos', { defaultValue: 'Suscritos' })} value={<Badge tone="success">{conteo.suscritos}</Badge>} />
        <KVRow label={t('newsletter.deBaja', { defaultValue: 'De baja' })} value={<Badge tone="muted">{conteo.deBaja}</Badge>} />
        <KVRow label={t('newsletter.rebotados', { defaultValue: 'Rebotados' })} value={<Badge tone={conteo.rebotados > 0 ? 'amber' : 'muted'}>{conteo.rebotados}</Badge>} />
        <KVRow label={t('newsletter.quejas', { defaultValue: 'Quejas de spam' })} value={<Badge tone={conteo.quejas > 0 ? 'danger' : 'muted'}>{conteo.quejas}</Badge>} />
        <KVRow label={t('newsletter.remitente', { defaultValue: 'Sale como' })} value={audiencia.fromAddress} last />
      </DetailSection>

      <DetailSection
        title={t('newsletter.automatico.title', { defaultValue: 'Envío automático' })}
        collapsible
        storageKey={`newsletter-automatico:${String(projectId)}`}
      >
        <Toggle
          checked={audiencia.autoSend}
          onChange={(v) => { void alternarAutomatico(v); }}
          disabled={fijarAutomatico.isPending}
          label={t('newsletter.automatico.label', { defaultValue: 'Enviar los artículos nuevos al publicarse' })}
          description={audiencia.autoSend
            ? t('newsletter.automatico.activoDesde', { defaultValue: 'Activo desde {{fecha}}', fecha: fecha(audiencia.autoSendEnabledAt) })
            : t('newsletter.automatico.apagadoDesc', { defaultValue: 'Apagado: cada artículo se envía a mano desde la lista de abajo.' })}
        />
      </DetailSection>

      {/* Lo pendiente se ve con la tarjeta cerrada: si se dejó plegada y
          después se publica un artículo, el aviso está en la cabecera. Los
          artículos llegan en otra consulta: hasta que estén, porEnviar es 0 y
          la tarjeta arrancaría cerrada, así que se monta de nuevo al llegar. */}
      <DetailSection
        key={arts ? 'articulos' : 'articulos-cargando'}
        title={t('newsletter.articulos.title', { defaultValue: 'Artículos' })}
        count={articulos.length}
        collapsible
        defaultOpen={porEnviar > 0}
        storageKey={`newsletter-articulos:${String(projectId)}`}
        {...(porEnviar > 0 ? { titleRight: <Badge tone="cyan">{t('newsletter.articulos.porEnviar', { count: porEnviar })}</Badge> } : {})}
      >
        {articulos.length === 0
          ? <EmptyState size="sm" title={t('newsletter.articulos.vacio', { defaultValue: 'Este proyecto no tiene artículos' })} />
          : articulos.map(a => (
            <ListItem
              key={a.id}
              title={a.title}
              subtitle={subtituloDelArticulo(a)}
              badges={[
                <Badge key="e" tone={a.envio ? 'success' : a.status === 'publicado' ? 'cyan' : 'muted'}>
                  {a.envio
                    ? t('newsletter.estado.sent', { defaultValue: 'Enviado' })
                    : t(`newsletter.articulos.estado.${a.status}`, { defaultValue: a.status })}
                </Badge>,
              ]}
              alwaysShowActions
              {...(!a.envio && a.status === 'publicado'
                ? {
                  actions: (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { void mandar(a); }}
                      loading={enviar.isPending}
                    >
                      {t('newsletter.articulos.enviar', { defaultValue: 'Enviar' })}
                    </Button>
                  ),
                }
                : {})}
            />
          ))}
      </DetailSection>

      <DetailSection
        title={t('newsletter.correos.title', { defaultValue: 'Correos de la lista' })}
        count={enLaLista}
        collapsible
        defaultOpen={false}
        storageKey={`newsletter-correos:${String(projectId)}`}
      >
        <div style={{ marginBottom: 'var(--evo-space-3)' }}>
          <SegmentedControl
            value={filtro}
            onChange={(v) => { setFiltro(v as EstadoMiembro); setTodos(false); }}
            options={[
              { value: 'subscribed', label: t('newsletter.suscritos', { defaultValue: 'Suscritos' }) },
              { value: 'unsubscribed', label: t('newsletter.deBaja', { defaultValue: 'De baja' }) },
              { value: 'bounced', label: t('newsletter.rebotados', { defaultValue: 'Rebotados' }) },
              { value: 'complained', label: t('newsletter.quejas', { defaultValue: 'Quejas' }) },
            ]}
          />
        </div>

        {visibles.length === 0
          ? <EmptyState size="sm" title={t('newsletter.correos.vacio', { defaultValue: 'No hay correos en este estado' })} />
          : visibles.map(m => (
            <ListItem
              key={m.id}
              title={m.email}
              {...(m.name ?? m.consentSource ? { subtitle: m.name ?? m.consentSource! } : {})}
              badges={[<Badge key="s" tone={TONO_MIEMBRO[m.status]}>{t(`newsletter.miembro.${m.status}`, { defaultValue: m.status })}</Badge>]}
              {...(m.unsubscribedAt ? { meta: fecha(m.unsubscribedAt) } : {})}
            />
          ))}

        {!todos && miembros.length > PRIMEROS && (
          <div style={{ marginTop: 'var(--evo-space-3)' }}>
            <Button variant="ghost" tone="muted" size="sm" onClick={() => setTodos(true)}>
              {t('newsletter.correos.verTodos', {
                defaultValue: 'Ver los {{n}}', n: miembros.length,
              })}
            </Button>
          </div>
        )}
      </DetailSection>

      <DetailSection
        title={t('newsletter.envios.title', { defaultValue: 'Historial de envíos' })}
        count={envios.length}
        collapsible
        defaultOpen={false}
        storageKey={`newsletter-envios:${String(projectId)}`}
      >
        {envios.length === 0
          ? <EmptyState size="sm" title={t('newsletter.envios.vacio', { defaultValue: 'Todavía no se ha enviado nada' })} />
          : envios.map(e => (
            <ListItem
              key={e.id}
              title={e.subject}
              subtitle={t('newsletter.envios.resumen', {
                defaultValue: '{{entregados}} entregados · {{aperturas}} lo abrieron · {{clics}} pulsaron · {{fallidos}} fallidos',
                entregados: e.destinatarios, aperturas: e.aperturas, clics: e.clics, fallidos: e.fallidos,
              })}
              meta={fecha(e.sentAt ?? e.createdAt)}
              badges={[
                <Badge key="estado" tone={TONO_ENVIO[e.status] ?? 'neutral'}>
                  {t(`newsletter.estado.${e.status}`, { defaultValue: e.status })}
                </Badge>,
              ]}
            />
          ))}
      </DetailSection>

      {importando && (
        <ImportarListaModal projectId={projectId} necesitaRemitente={false} onClose={() => setImportando(false)} />
      )}
      {confirmDialog}
    </>
  );
}
